/**
 * Input - T044
 *
 * Styled input field with label and error states
 */
import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
  icon?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, hint, icon, className = '', id, ...props }, ref) => {
    const generatedId = useId();
    const inputId = id || props.name || generatedId;
    const feedbackId = `${inputId}-feedback`;

    return (
      <div className="w-full">
        {label && (
          <label htmlFor={inputId} className="block text-sm font-medium text-white/70 mb-1.5">
            {label}
          </label>
        )}
        <div className="relative">
          {icon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40">{icon}</div>
          )}
          <input
            ref={ref}
            id={inputId}
            className={`
              w-full px-4 py-3 rounded-lg
              bg-white/[0.03] border border-white/[0.08]
              text-white placeholder:text-white/30
              focus:outline-none focus:border-white/20 focus:bg-white/[0.05]
              transition-colors duration-200
              disabled:opacity-50 disabled:cursor-not-allowed
              ${icon ? 'pl-11' : ''}
              ${error ? 'border-red-500/50 focus:border-red-500/50' : ''}
              ${className}
            `}
            {...props}
            aria-invalid={error ? true : props['aria-invalid']}
            aria-describedby={
              [props['aria-describedby'], error || hint ? feedbackId : null]
                .filter(Boolean)
                .join(' ') || undefined
            }
          />
        </div>
        {error && (
          <p id={feedbackId} role="alert" className="mt-1.5 text-sm text-red-400">
            {error}
          </p>
        )}
        {hint && !error && (
          <p id={feedbackId} className="mt-1.5 text-sm text-white/40">
            {hint}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
